import { useCallback, useEffect, useRef } from "react";
import { OilPainting } from "./OilPainting";
import { useGazeData } from "../../hooks/useGazeData";
import styles from "./oil-painting-example.module.scss";

export function OilPaintingExample() {
  const oilPaintingRef = useRef(null); // Store OilPainting instance
  const gazePosRef = useRef(null); // Store latest gaze position
  const canvasRef = useRef(null);
  const lastTimestampRef = useRef(null);
  const animationFrameIdRef = useRef(null);
  const gazeHistoryRef = useRef([]); // Store recent gaze positions for smoothing
  const SMOOTHING_WINDOW = 3; // Number of points to average
  const INTERPOLATION_STEPS = 5; // Number of intermediate points to draw

  useGazeData(({ x, y }) => {
    const newPos = { x, y, timestamp: Date.now() };

    // Add to smoothing buffer
    gazeHistoryRef.current.push(newPos);
    if (gazeHistoryRef.current.length > SMOOTHING_WINDOW) {
      gazeHistoryRef.current.shift();
    }

    // Calculate smoothed position
    const smoothedX = gazeHistoryRef.current.reduce((sum, pos) => sum + pos.x, 0) / gazeHistoryRef.current.length;
    const smoothedY = gazeHistoryRef.current.reduce((sum, pos) => sum + pos.y, 0) / gazeHistoryRef.current.length;

    gazePosRef.current = { x: smoothedX, y: smoothedY, timestamp: Date.now() };
  });

  const animationFrameCallback = useCallback(() => {
    animationFrameIdRef.current = requestAnimationFrame(() => {
      if (!oilPaintingRef.current || !gazePosRef.current) {
        animationFrameCallback();
        return;
      }

      const currentPos = gazePosRef.current;

      // Instead of using timestamps it would be preferable to
      // listen to a starvation event or similar from the gaze data provider
      if (lastTimestampRef.current === currentPos.timestamp) {
        animationFrameCallback();
        return;
      }

      const prevPos = oilPaintingRef.current.prevPos;

      // Interpolate between previous and current position for smoother drawing
      if (prevPos && (prevPos.x !== currentPos.x || prevPos.y !== currentPos.y)) {
        for (let i = 1; i <= INTERPOLATION_STEPS; i++) {
          const t = i / INTERPOLATION_STEPS;
          const interpX = prevPos.x + (currentPos.x - prevPos.x) * t;
          const interpY = prevPos.y + (currentPos.y - prevPos.y) * t;
          oilPaintingRef.current.drawAt(interpX, interpY);
        }
      } else {
        // First point or no movement
        oilPaintingRef.current.drawAt(currentPos.x, currentPos.y);
      }

      lastTimestampRef.current = currentPos.timestamp;

      // Change color when gaze moves out of bounds
      // Consider adding buttons or using an interval timer instead
      if (
        currentPos.x < 0 ||
        currentPos.y < 0 ||
        currentPos.x > window.innerWidth ||
        currentPos.y > window.innerHeight
      ) {
        oilPaintingRef.current.setRandomStrokeColor();
      }

      animationFrameCallback();
    });
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;

    const canvas = canvasRef.current;
    oilPaintingRef.current = new OilPainting(canvas);
    animationFrameCallback();

    return () => {
      if (animationFrameIdRef.current) {
        cancelAnimationFrame(animationFrameIdRef.current);
      }

      oilPaintingRef.current.dispose();
      oilPaintingRef.current = null;
    };
  }, [animationFrameCallback]);

  return (
    <>
      <canvas data-tdx-mouseless ref={canvasRef} aria-description="Oil painting canvas" className={styles.canvas} />
    </>
  );
}
